import React from 'react';
import fetchUser from '../../../../utils/gaia/fetchUser';
import useSyncPromise from '../../../../utils/react/useSyncPromise';
import GridSelect from './GridSelect';
import {LayersSidebarProps} from '../LayersSidebar';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import {makeStyles} from '@material-ui/styles';
import {Sidebar, SidebarBody, SidebarHead} from '../Sidebar';
import Switch from '@material-ui/core/Switch';
import trackAnalytics from '../../../../utils/gaia/trackAnalytics';
import theme from '../../../../theme';

const useStyles = makeStyles({
    root: {
        marginRight: theme.spacing(-1)
    }
});

export interface OverlaysSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    onSidebarChange: (sidebar: MapSidebar) => void;
}

export default function OverlaysSidebar({
    onOverlaysChange,
    overlays,
    interactionType,
    disableShowTracks,
    disableShowWaypoints,
    disableShowAreas,
    onGridChange,
    grid,
    ...props
}: LayersSidebarProps) {
    const [user] = useSyncPromise(fetchUser());
    const classes = useStyles();

    const waypointsDisabled = disableShowWaypoints || !user || !user.isAuthenticated ||
        interactionType === 'movingWaypoint';

    const tracksDisabled = disableShowTracks || !user || !user.isAuthenticated ||
        interactionType === 'editingRoute';

    const areasDisabled = disableShowAreas || !user || !user.isAuthenticated ||
        interactionType === 'movingWaypoint' || interactionType === 'editingRoute';

    return <Sidebar {...props}>
        <SidebarHead
            onSidebarChange={props.onSidebarChange}
            prevSidebar={{type: 'layers'}}
            title={'Map Overlays'}
        />
        <SidebarBody>
            <List disablePadding>
                <ListItem divider>
                    <ListItemText primary='Waypoints' />
                    <ListItemSecondaryAction>
                        <Switch
                            color='primary'
                            classes={{root: classes.root}}
                            checked={overlays.showWaypoints}
                            disabled={waypointsDisabled}
                            onChange={event => {
                                if (event.target.checked) {
                                    trackAnalytics('Add Waypoints Overlay', {category: 'map2'});
                                } else {
                                    trackAnalytics('Remove Waypoints Overlay', {category: 'map2'});
                                }
                                onOverlaysChange({
                                    ...overlays,
                                    showWaypoints: event.target.checked,
                                });
                            }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider>
                    <ListItemText primary='Waypoint Labels' />
                    <ListItemSecondaryAction>
                        <Switch
                            color='primary'
                            classes={{root: classes.root}}
                            checked={overlays.showWaypointLabels}
                            disabled={waypointsDisabled}
                            onChange={event => {
                                if (event.target.checked) {
                                    trackAnalytics('Add Waypoint Labels Overlay', {
                                        category: 'map2'
                                    });
                                } else {
                                    trackAnalytics('Remove Waypoint Labels Overlay', {
                                        category: 'map2'
                                    });
                                }
                                onOverlaysChange({
                                    ...overlays,
                                    showWaypointLabels: event.target.checked,
                                });
                            }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider>
                    <ListItemText primary='Routes' />
                    <ListItemSecondaryAction style={{
                    }}>
                        <Switch
                            color='primary'
                            classes={{root: classes.root}}
                            checked={overlays.showRoutes}
                            disabled={tracksDisabled}
                            onChange={event => {
                                if (event.target.checked) {
                                    trackAnalytics('Add Routes Overlay', {category: 'map2'});
                                } else {
                                    trackAnalytics('Remove Routes Overlay', {category: 'map2'});
                                }
                                onOverlaysChange({
                                    ...overlays,
                                    showRoutes: event.target.checked,
                                });
                            }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider>
                    <ListItemText primary='Tracks' />
                    <ListItemSecondaryAction style={{
                    }}>
                        <Switch
                            color='primary'
                            classes={{root: classes.root}}
                            checked={overlays.showTracks}
                            disabled={tracksDisabled}
                            onChange={event => {
                                if (event.target.checked) {
                                    trackAnalytics('Add Tracks Overlay', {category: 'map2'});
                                } else {
                                    trackAnalytics('Remove Tracks Overlay', {category: 'map2'});
                                }
                                onOverlaysChange({
                                    ...overlays,
                                    showTracks: event.target.checked,
                                });
                            }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider>
                    <ListItemText primary='Areas' />
                    <ListItemSecondaryAction>
                        <Switch
                            color='primary'
                            classes={{root: classes.root}}
                            checked={overlays.showAreas}
                            disabled={areasDisabled}
                            onChange={event => {
                                if (event.target.checked) {
                                    trackAnalytics('Add Areas Overlay', {category: 'map2'});
                                } else {
                                    trackAnalytics('Remove Areas Overlay', {category: 'map2'});
                                }
                                onOverlaysChange({
                                    ...overlays,
                                    showAreas: event.target.checked,
                                });
                            }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider>
                    <ListItemText primary='Public Tracks' />
                    <ListItemSecondaryAction>
                        <Switch
                            color='primary'
                            classes={{root: classes.root}}
                            checked={overlays.showPublicTracks}
                            onChange={event => {
                                if (event.target.checked) {
                                    trackAnalytics('Add Public Tracks Overlay', {category: 'map2'});
                                } else {
                                    trackAnalytics('Remove Public Tracks Overlay', {
                                        category: 'map2'
                                    });
                                }
                                onOverlaysChange({
                                    ...overlays,
                                    showPublicTracks: event.target.checked,
                                });
                            }}
                        />
                    </ListItemSecondaryAction>
                </ListItem>

                <ListItem divider>
                    <ListItemText primary='Grid Overlay' style={{alignSelf: 'center'}} />
                    <GridSelect
                        onGridChange={onGridChange}
                        grid={grid}
                        style={{marginRight: theme.spacing(1)}}
                    />
                </ListItem>
            </List>
        </SidebarBody>
    </Sidebar>;
}
